import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';
import { canUseCrashReports } from '@/services/consent';
import type { ConsentSettings } from '@/services/consent';
import { SENTRY_DSN, SENTRY_RELEASE, SENTRY_ENVIRONMENT } from './buildInfo';

// Sentry is initialized HERE (imported from index.js, the JS entry) instead of
// app/_layout.tsx. Why this matters:
//
// A headless background-location JobService relaunch (Android delivering a
// background location batch) starts a fresh JS context but NEVER evaluates
// expo-router route modules like app/_layout.tsx. With Sentry.init living in
// _layout.tsx, the native crash handler was therefore left UNARMED during
// background task execution — so the single biggest production crash
// (expo.modules.taskManager.TaskService.executeTask NPE: 80+ users, all
// devices/Android versions) was invisible to Sentry and only ever showed up in
// the Play Console. index.js runs on EVERY JS context start (foreground and
// headless), so initializing from here arms the native handler in both — which
// is also Sentry's own recommended placement (init as early as possible).
//
// Idempotent: index.js imports this once per context; the guard is belt-and-
// suspenders against an accidental second import.

// Crash-report consent (#247, decisão 2026-07-02): crash reporting is OPT-OUT
// (legitimate interest). Sentry.init stays synchronous and unconditional (see
// block above — deferring/skipping init would disarm the native handler in
// headless contexts); the consent gate lives in beforeSend, which drops error
// events client-side once the user explicitly opts out in the consent banner
// or Conta → Privacidade. Undecided users (no stored consent) keep crash
// reporting on. Sessions/breadcrumbs remain as legitimate-interest telemetry;
// only error events (which carry user context) are gated.
let crashReportsAllowed = true;

let initialized = false;

export function initSentry(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,

    // Never send events from local dev bundles (Metro). Dev sessions were leaking
    // real issues into the project (ALLDRIVERS-REACT-NATIVE-46: localhost:8081,
    // -3J: TransformError with a dev worktree path, -3G). __DEV__ is compiled
    // false in every release bundle, so this cannot silence production.
    enabled: !__DEV__,

    // Separate prod / preview / dev streams so the dashboards (and regression
    // detection) for the alldrivers-web and alldrivers-react-native projects are
    // not polluted by non-production events. APP_VARIANT production|preview|development
    // maps to production|staging|development, the backend's names (sentryEnvironment.ts).
    environment: SENTRY_ENVIRONMENT,

    // Match the dist tag used when sourcemaps are uploaded: the mobile upload
    // (scripts/eas-update-safe.js) runs `sentry-cli sourcemaps upload --dist <platform>`
    // with platform = ios|android, exactly what Platform.OS returns. Symbolication
    // itself is keyed by injected Debug IDs (sourcemaps inject), so this is for
    // consistent artifact/release-health grouping, not for source mapping. Web
    // uploads no dist; 'web' here is harmless (Debug IDs still resolve web frames).
    dist: Platform.OS,

    // Release Health / sessions. On web, @sentry/react-native only adds
    // browserSessionIntegration when this flag is truthy, and it is NOT in the
    // SDK's DEFAULT_OPTIONS — so leaving it unset means the web project never
    // receives sessions (native sessions are tracked by the native SDK
    // independently, which is why mobile worked but alldrivers-web showed none).
    enableAutoSessionTracking: true,

    // Keep verbose SDK logs in dev only.
    enableLogs: __DEV__,

    // Drop non-actionable, self-recovering browser warnings so they don't show up
    // as "errors" and dilute the real-bug count. These are NOT the WEB-5 stack
    // overflow (a genuine synchronous ResizeObserver re-entry fixed in
    // mymap.web.js) — they are the layout engine reflowing on the next frame,
    // which the browser handles on its own.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Browser-extension / injected-script noise (web). MetaMask inpage script
      // (ALLDRIVERS-WEB-12) and DOM mutations from Google Translate & co. that
      // break React's commitDeletionEffects (ALLDRIVERS-WEB-R). Not app code.
      'Failed to connect to MetaMask',
      "Failed to execute 'removeChild' on 'Node'",
      "Failed to execute 'insertBefore' on 'Node'",
    ],

    // Switched off with the consent gate (#247): IP/cookies/user PII are not
    // needed for crash triage and stay out of events regardless of consent.
    sendDefaultPii: false,

    // Session Replay is fully disabled on every platform:
    //   - Web: mobileReplayIntegration recurses into the DOM and triggers
    //     "Maximum call stack size exceeded" (ALLDRIVERS-WEB-5).
    //   - Android: ReplayIntegration.start contends on an internal ReentrantLock
    //     during ProcessLifecycleOwner.onActivityPostStarted, parking the main
    //     thread and producing ANRs (ALLDRIVERS-REACT-NATIVE-2H, 69 events as
    //     of 2026-05-26, regressed). Stacktrace pins the lock at
    //     io.sentry.android.replay.ReplayIntegration.start.
    //   - iOS: disabled here defensively so the SDK config matches across mobile
    //     platforms. Re-enable per-platform once Sentry RN ships a fix.
    // Crash reporting + breadcrumbs stay intact via the default integrations.
    integrations: [Sentry.feedbackIntegration()],

    beforeSend(event) {
      // Consent gate (#247): user explicitly opted out of crash reports —
      // drop every error event client-side. The SDK stays armed so opting
      // back in takes effect without a restart.
      if (!crashReportsAllowed) {
        return null;
      }

      // Drop web events that carry no attributable frame. ALLDRIVERS-WEB-5
      // ("Maximum call stack size exceeded", 1080 events): every event in 90 days
      // is Chrome Mobile iOS with a single frame of `app:///undefined` from the
      // global onerror handler — the signature of a script the BROWSER injected
      // into the page (Chrome iOS __gCrWeb autofill/translate), not our bundle.
      // Safari iOS shares the same WebKit engine and never reports it, which rules
      // out app code. Two earlier fixes (replay disable, mymap.web ResizeObserver)
      // shipped and changed nothing because there was never an app frame to fix.
      // Only events whose frames ALL lack a resolvable filename are dropped — any
      // real error in our bundle always carries the bundle filename.
      if (Platform.OS === 'web') {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (
          frames &&
          frames.length > 0 &&
          frames.every(
            (f) =>
              !f.filename ||
              f.filename === 'app:///undefined' ||
              f.filename === '<anonymous>'
          )
        ) {
          return null;
        }
      }
      return event;
    },

    // Drop low-signal console breadcrumbs in production.
    beforeBreadcrumb(breadcrumb) {
      if (!__DEV__ && breadcrumb.category === 'console') {
        const level = breadcrumb.level;
        if (level === 'log' || level === 'info' || level === 'debug') {
          return null;
        }
      }
      return breadcrumb;
    },

    // uncomment the line below to enable Spotlight (https://spotlightjs.com)
    // spotlight: __DEV__,
  });
}

// Applies stored/updated consent to the running SDK. Never inits or tears the
// SDK down — see the opt-out note above; gating is done in beforeSend.
export async function applySentryConsent(consent?: ConsentSettings | null): Promise<void> {
  if (consent !== undefined && consent !== null) {
    crashReportsAllowed = consent.crashReports;
    return;
  }
  crashReportsAllowed = await canUseCrashReports();
}

export const resetSentryInitForTests = (): void => {
  initialized = false;
  crashReportsAllowed = true;
};

initSentry();
// Hydrate the consent flag asynchronously. Until AsyncStorage resolves the
// default stays "allowed" (opt-out semantics), so undecided users and headless
// contexts never lose crash coverage during startup.
void applySentryConsent();
